import { useEffect, useRef, useState } from 'react';
import type { Viewer } from '../types';
import { colors } from '../constants/colors';
import { getPublicProfile } from '../api/client';
import { UserAvatar } from './UserAvatar';
import { UserActionMenu } from './UserActionMenu';

interface ViewerListProps {
  viewers: Viewer[];
  /** When provided, clicking a viewer opens the moderation menu (block/kick/
   *  ban/message). Pass the show id and the seller flag for the menu. */
  showId?: string;
  isSeller?: boolean;
  /** Auth user id — used to skip the menu on the seller's own row if they
   *  ever appear in the viewer list. */
  currentUserId?: string;
}

interface SelectedUser {
  id: string;
  name: string;
  anchorTop: number;
  anchorLeft: number;
}

// Module-level cache so avatars persist across tab switches without refetching.
const avatarCache = new Map<string, string | null>();

export function ViewerList({ viewers, showId, isSeller = false, currentUserId }: ViewerListProps) {
  const [, force] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);

  // Fetch any avatars we don't have cached yet. Each lookup is keyed by
  // username; a null entry means "we already tried and there's no avatar".
  useEffect(() => {
    const missing = viewers.filter(
      (v) => v.username && !avatarCache.has(v.username),
    );
    if (missing.length === 0) return;

    let cancelled = false;
    Promise.allSettled(
      missing.map((v) =>
        getPublicProfile(v.username!).then((profile) => {
          avatarCache.set(v.username!, profile.avatarUrl ?? null);
        }).catch(() => {
          avatarCache.set(v.username!, null);
        }),
      ),
    ).then(() => {
      if (!cancelled) force((n) => n + 1);
    });

    return () => { cancelled = true; };
  }, [viewers]);

  const handleViewerClick = (e: React.MouseEvent, viewer: Viewer) => {
    if (currentUserId && viewer.userId === currentUserId) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    setSelectedUser({
      id: viewer.userId,
      name: viewer.username || `User ${viewer.userId.slice(0, 6)}`,
      anchorTop: rect.bottom - containerRect.top,
      anchorLeft: rect.left - containerRect.left,
    });
  };

  return (
    <div style={styles.container} ref={containerRef}>
      <div style={styles.list}>
        {viewers.length === 0 ? (
          <p style={styles.empty}>No viewers yet</p>
        ) : (
          viewers.map((viewer) => {
            const avatarUrl = viewer.username
              ? avatarCache.get(viewer.username) ?? null
              : null;
            const isSelf = currentUserId && viewer.userId === currentUserId;
            return (
              <div
                key={viewer.userId}
                style={{ ...styles.viewer, cursor: isSelf ? 'default' : 'pointer' }}
                onClick={(e) => handleViewerClick(e, viewer)}
              >
                <UserAvatar
                  src={avatarUrl}
                  size={32}
                  alt={viewer.username || 'Viewer'}
                />
                <span style={styles.username}>
                  {viewer.username || `User ${viewer.userId.slice(0, 6)}`}
                </span>
              </div>
            );
          })
        )}
      </div>

      <UserActionMenu
        user={selectedUser ? { id: selectedUser.id, name: selectedUser.name } : null}
        position={selectedUser ? { top: selectedUser.anchorTop, left: selectedUser.anchorLeft } : { top: 0, left: 0 }}
        isSeller={isSeller}
        showId={showId}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  list: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflowY: 'auto',
  },
  empty: {
    color: '#888',
    fontSize: '14px',
    textAlign: 'center',
    margin: '16px 0',
  },
  viewer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    backgroundColor: colors.white,
    borderRadius: '6px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
  },
  username: {
    fontSize: '14px',
    color: colors.ink.black,
  },
};
